/**
 * O que faz: Formata indicadores de métricas para exibição (valor, base e explicação).
 * Onde usa: ResultadoCanalDashboard, cards e modais de detalhe.
 * Conexões: Tipos IndicadorMetrica do contrato compartilhado.
 * Tabelas: Nenhuma (formatação em memória).
 * Data: 05/09/2026.
 */

import type { IndicadorMetrica } from '../../../supabase/functions/_shared/metricas/contratos';

export interface IndicadorApresentado {
  valor: string;
  base: string | null;
  estado: IndicadorMetrica['estado'];
  explicacao: string | null;
}

const formatoNumero = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const formatoInteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const formatoMoeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function formatarDuracao(segundos: number): string {
  // Duração curta mostra minutos; longa mostra horas para leitura rápida.
  if (segundos < 3600) return `${formatoInteiro.format(Math.round(segundos / 60))} min`;
  return `${formatoNumero.format(segundos / 3600)} h`;
}

export function apresentarIndicador(indicador: IndicadorMetrica): IndicadorApresentado {
  // Indisponível nunca inventa número; mostra “—” com o motivo da fonte.
  if (indicador.estado === 'unavailable' || indicador.valor === null) {
    return {
      valor: '—',
      base: indicador.denominador !== null && indicador.numerador !== null
        ? `${formatoInteiro.format(indicador.numerador)} de ${formatoInteiro.format(indicador.denominador)}`
        : null,
      estado: indicador.estado,
      explicacao: indicador.motivo ?? 'Sem base de comparação',
    };
  }
  let valor: string;
  switch (indicador.unidade) {
    case 'percentual':
      valor = `${formatoNumero.format(indicador.valor)}%`;
      break;
    case 'centavos':
      valor = formatoMoeda.format(indicador.valor / 100);
      break;
    case 'segundos':
      valor = formatarDuracao(indicador.valor);
      break;
    case 'razao':
      valor = `${formatoNumero.format(indicador.valor)}×`;
      break;
    default:
      valor = formatoInteiro.format(indicador.valor);
  }
  // Taxa sempre mostra o denominador para explicar a base da conta.
  const base = indicador.denominador !== null && indicador.numerador !== null
    ? `${formatoInteiro.format(indicador.numerador)} de ${formatoInteiro.format(indicador.denominador)}`
    : null;
  return { valor, base, estado: indicador.estado, explicacao: indicador.motivo };
}

export function corVariacao(melhorQuando: IndicadorMetrica['melhorQuando'], variacaoAbsoluta: number | null): 'positiva' | 'negativa' | 'neutra' {
  // Aumento de falha ou tempo não recebe verde; a cor deriva do melhorQuando.
  if (variacaoAbsoluta === null || variacaoAbsoluta === 0) return 'neutra';
  const subiu = variacaoAbsoluta > 0;
  if (melhorQuando === 'neutro') return 'neutra';
  if (melhorQuando === 'sobe') return subiu ? 'positiva' : 'negativa';
  return subiu ? 'negativa' : 'positiva';
}
